import { defineStore } from 'pinia';
import { useUpdateStatusStore } from './updateStatus';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, Ref } from 'vue';
import { IRubricDTO, IRubricFieldDTO, IRubricSectionDTO, ISubmissionRubricFormDTO } from '@/dtos';
import { createSubmissionEntryFormAPI, getRubricByYearAPI } from '@/services';
import {
  createRubricFieldAPI,
  createRubricForYearAPI,
  createRubricSectionAPI,
  createSubmissionRubricFormAPI,
  deleteRubricFieldAPI,
  deleteRubricSectionAPI,
  updateRubricAPI,
  updateRubricFieldsAPI,
  updateRubricSectionsAPI,
  updateSubmissionRubricFormAPI,
} from '@/services/admin';

export const useRubricStore = defineStore('rubric', () => {
  const statusStore = useUpdateStatusStore();
  const queryClient = useQueryClient();

  const getRubricByYearQuery = (yearId: Ref<string>) => {
    return useQuery({
      queryKey: computed(() => ['rubric-form', yearId.value]),
      queryFn: async (): Promise<IRubricDTO> => {
        const ret = await getRubricByYearAPI(yearId.value);
        ret.sections.sort((a, b) => a.order - b.order);
        ret.sections.forEach((section) => {
          section.fields.sort((a, b) => a.order - b.order);
        });
        return ret;
      },
      enabled: computed(() => !!yearId.value),
    });
  };

  // Mutations
  const createRubricForYear = useMutation({
    mutationFn: (yearId: string) => {
      return createRubricForYearAPI(yearId);
    },
    onSuccess: (_data, yearId) => {
      queryClient.invalidateQueries({ queryKey: ['rubric-form', yearId] });
      statusStore.setSuccessStatus('rubric-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`rubric-create`, err);
    },
  });

  const updateRubric = useMutation({
    mutationFn: (Rubric: IRubricDTO) => {
      return updateRubricAPI(Rubric);
    },
    onSuccess: (_data, Rubric: IRubricDTO) => {
      queryClient.invalidateQueries({ queryKey: ['rubric-form'] });
      statusStore.setSuccessStatus(`rubric-${Rubric.id}`, 'success:update');
    },
    onError: (err: string, Rubric: IRubricDTO) => {
      statusStore.setErrorStatus(`rubric-${Rubric.id}`, err);
    },
  });

  const createSection = useMutation({
    mutationFn: ({ section }: { section: IRubricSectionDTO; yearId: string }) => {
      return createRubricSectionAPI(section);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rubric-form', variables.yearId] });
      statusStore.setSuccessStatus('rubric-section-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`rubric-section-create`, err);
    },
  });

  const updateSections = useMutation({
    mutationFn: ({
      sections,
      RubricId,
    }: {
      sections: IRubricSectionDTO[];
      RubricId: string;
      yearId: string;
    }) => {
      return updateRubricSectionsAPI(RubricId, sections);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rubric-form', variables.yearId] });
      statusStore.setSuccessStatus(`rubric-section-update-${variables.RubricId}`, 'success:update');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`rubric-section-update-${variables.RubricId}`, err);
    },
  });

  const updateSection = useMutation({
    mutationFn: ({
      section,
      RubricId,
    }: {
      section: IRubricSectionDTO;
      RubricId: string;
      yearId: string;
    }) => {
      let sections = [];
      sections.push(section);
      return updateRubricSectionsAPI(RubricId, sections);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rubric-form', variables.yearId] });
      statusStore.setSuccessStatus(
        `rubric-section-update-${variables.section.id}`,
        'success:update',
      );
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`rubric-section-update-${variables.section.id}`, err);
    },
  });

  const removeSection = useMutation({
    mutationFn: ({ section }: { section: IRubricSectionDTO; yearId: string }) => {
      return deleteRubricSectionAPI(section);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rubric-form', variables.yearId] });
      statusStore.setSuccessStatus(
        `rubric-section-update-${variables.section.id}`,
        'success:delete',
      );
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`rubric-section-update-${variables.section.id}`, err);
    },
  });

  const createField = useMutation({
    mutationFn: ({ field }: { field: IRubricFieldDTO; yearId: string }) => {
      return createRubricFieldAPI(field);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rubric-form', variables.yearId] });
      statusStore.setSuccessStatus('rubric-field-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`rubric-field-create`, err);
    },
  });

  const updateField = useMutation({
    mutationFn: ({
      field,
      rubricId: RubricId,
      sectionId,
    }: {
      field: IRubricFieldDTO;
      rubricId: string;
      sectionId: string;
      yearId: string;
    }) => {
      let fields: IRubricFieldDTO[] = [];
      fields.push(field);
      return updateRubricFieldsAPI(RubricId, sectionId, fields);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rubric-form', variables.yearId] });
      statusStore.setSuccessStatus(`rubric-field-update-${variables.field.id}`, 'success:update');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`rubric-field-update-${variables.field.id}`, err);
    },
  });

  const removeField = useMutation({
    mutationFn: ({ field }: { field: IRubricFieldDTO; yearId: string }) => {
      return deleteRubricFieldAPI(field);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rubric-form', variables.yearId] });
      statusStore.setSuccessStatus(`rubric-field-update-${variables.field.id}`, 'success:delete');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`rubric-field-update-${variables.field.id}`, err);
    },
  });

  const updateFields = useMutation({
    mutationFn: ({
      fields,
      rubricId: rubricId,
      sectionId,
    }: {
      fields: IRubricFieldDTO[];
      rubricId: string;
      sectionId: string;
      yearId: string;
    }) => {
      return updateRubricFieldsAPI(rubricId, sectionId, fields);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rubric-form', variables.yearId] });
      statusStore.setSuccessStatus(`rubric-field-update-${variables.sectionId}`, 'success:update');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`rubric-fields-update-${variables.sectionId}`, err);
    },
  });

  const createRubricSubmission = useMutation({
    mutationFn: (submissionId: string) => {
      return createSubmissionRubricFormAPI(submissionId);
    },
    onSuccess: (_data, submissionId) => {
      queryClient.invalidateQueries({ queryKey: ['submission-rubric', submissionId] });
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`submission-rubric`, err);
    },
  });

  const updateRubricSubmission = useMutation({
    mutationFn: ({
      submissionId,
      submissionRubricForm,
    }: {
      submissionId: string;
      submissionRubricForm: ISubmissionRubricFormDTO;
    }) => {
      return updateSubmissionRubricFormAPI(submissionId, submissionRubricForm);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submission-rubric', variables.submissionId] });
      statusStore.setSuccessStatus(`submission-rubric-${variables.submissionId}`, 'success:update');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`submission-rubric-${variables.submissionId}`, err);
    },
  });

  return {
    getRubricByYearQuery,
    createRubricForYear,
    createSection,
    updateSection,
    updateSections,
    removeSection,
    updateRubric,
    createField,
    updateField,
    removeField,
    updateFields,
    createRubricSubmission,
    updateRubricSubmission,
  };
});
